/**
 * /mcp-connections — the signed-in tenant user's own MCP connections (backend metaplan A10; AIE §10
 * "MCP connections"; backend A7b; the console's twin is A7c's `/mcp-connections`). From the user menu.
 *
 * Each row is an OAuth grant the authorization server holds for an MCP client this user signed in to through
 * `/oauth/handoff`: the client and how far its name can be trusted, the access it got, when it was connected
 * and last refreshed, and the IP of that sign-in. Revoke ends the grant. The backend scopes both calls to the
 * session's user, so the page needs no permission. Contract: `../apiv2/authserver.openapi.yaml`
 * (`/v2/edit/oauth/connections/`).
 *
 * A 403 here (`tenant_required`: an ops session on the dashboard) is an answer, not an expired session: the
 * calls pass keepSessionOn403 (./notifications/aiChanges/api.js).
 *
 * Tenant-bound MCP URLs (backend plan 2026-10-20, P5): every grant is bound to one company and works only at its MCP
 * address (`resource`, e.g. `https://mcp.touroptima.com/v1/acme`); each row names both -- the company as it is now
 * (`bound_tenant_name` / `bound_tenant_code`) -- and badges a `stale` one (its address no longer names that company:
 * the MCP server refuses it). "Your MCP address" is the list's `mcp_addresses` -- the backend's own configuration, so
 * the page never builds a host itself -- shown only while `mcp_eligible` (the user can connect now; MCP is not
 * launched for everyone): otherwise "no MCP access yet" and no invitation to connect (REVIEW-P5-SPA M1).
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { DataAPIContext } from '../api';
import PageMeta from '../components/PageMeta';
import { AIEditApiError, errorCode, listConnections, revokeConnection } from '../notifications/aiChanges/api';
import { ContextStore } from '../store/contextStore';

const CLIENT_TYPE_LABEL = {
    cimd: 'Identified by its domain',
    static: 'Registered by Curated Planet',
    dcr: 'Self-registered',
};

/** "Read" / "Read and write" for the contract's `mcp:read` / `mcp:write`; other scopes as sent. */
export const accessLabel = (scopes) => {
    const list = Array.isArray(scopes) ? scopes : [];
    const known = list.filter((s) => s === 'mcp:read' || s === 'mcp:write');
    if (known.length === list.length && known.length > 0) {
        return list.includes('mcp:write') ? 'Read and write' : 'Read';
    }
    return list.length ? list.join(', ') : '—';
};

const fmt = (ts) => (ts ? moment(ts).format('YYYY-MM-DD HH:mm') : '—');

/** An address's company: "Name (code)" when the session knows it (its own operator or a franchise licensee), else the
 * code. */
const addressCompany = (code, { tourOperator, licensees } = {}) => {
    const known = [tourOperator, ...(Array.isArray(licensees) ? licensees : [])].filter(Boolean);
    const match = code ? known.find((t) => t.code === code) : undefined;
    if (match?.name) return `${match.name} (${code})`;
    return code || '—';
};

const Mono = ({ children }) => (
    <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 13, overflowWrap: 'anywhere' }}>
        {children}
    </Typography>
);

/** A grant's company: the bound tenant as it is now, "#<id>" when it is gone. */
const grantCompany = (c) => {
    if (c.bound_tenant_code) {
        return c.bound_tenant_name ? `${c.bound_tenant_name} (${c.bound_tenant_code})` : c.bound_tenant_code;
    }
    return c.bound_tenant_id != null ? `#${c.bound_tenant_id}` : '—';
};

/** "Your MCP address": what to add to an AI client, as the API names it -- only while the user can connect
 * (`eligible`); `undefined` (an older backend) shows nothing. */
const McpAddresses = ({ eligible, addresses, store }) => {
    const { enqueueSnackbar } = useSnackbar();
    if (eligible === false) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your account has no MCP access yet.
            </Typography>
        );
    }
    if (eligible !== true || !Array.isArray(addresses) || addresses.length === 0) return null;
    const copy = (url) => {
        const done = () => enqueueSnackbar('MCP address copied.', { variant: 'success' });
        const failed = () => enqueueSnackbar('Could not copy to clipboard.', { variant: 'error' });
        if (!navigator.clipboard) return failed();
        return navigator.clipboard.writeText(url).then(done, failed);
    };
    const several = addresses.length > 1;
    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2">{several ? 'Your MCP addresses' : 'Your MCP address'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {several ? 'Add one' : 'Add it'} to your AI client (Claude, Cursor, …) as an MCP server, then sign in
                with this account.
            </Typography>
            <Stack spacing={1}>
                {addresses.map((a) => (
                    <Box key={a.url} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Mono>{a.url}</Mono>
                            {several && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {addressCompany(a.tenant_code, store)}
                                </Typography>
                            )}
                        </Box>
                        <Tooltip title="Copy">
                            <IconButton size="small" onClick={() => copy(a.url)} aria-label={`Copy ${a.url}`}>
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ))}
            </Stack>
        </Paper>
    );
};

const ClientCell = ({ connection: c }) => (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                {c.client_name || c.client_id}
            </Typography>
            {!c.verified && (
                <Tooltip title="This client registered itself: its name is its own claim, not checked by Curated Planet. Only the address under it is verified.">
                    <Chip size="small" variant="outlined" color="warning" icon={<GppMaybeOutlinedIcon />} label="Unverified" />
                </Tooltip>
            )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {c.redirect_host ? `${c.redirect_host} · ` : ''}
            {CLIENT_TYPE_LABEL[c.client_type] ?? c.client_type}
        </Typography>
    </Stack>
);

const McpConnectionsPage = () => {
    const dataAPI = React.useContext(DataAPIContext);
    const { tourOperator, licensees } = React.useContext(ContextStore) || {};
    const store = { tourOperator, licensees };
    const { enqueueSnackbar } = useSnackbar();
    const [state, setState] = React.useState({
        loading: true, error: null, connections: [], addresses: undefined, eligible: undefined,
    });
    const [confirm, setConfirm] = React.useState(null);
    const [revoking, setRevoking] = React.useState(null);

    const load = React.useCallback(() => {
        setState((prev) => ({ ...prev, loading: true, error: null }));
        listConnections(dataAPI)
            .then((data) => setState({
                loading: false,
                error: null,
                connections: data?.connections || [],
                addresses: data?.mcp_addresses,
                eligible: data?.mcp_eligible,
            }))
            .catch((error) => setState({ loading: false, error, connections: [], addresses: undefined, eligible: undefined }));
    }, [dataAPI]);

    React.useEffect(() => {
        load();
    }, [load]);

    const doRevoke = (c) => {
        setRevoking(c.grant_id);
        revokeConnection(dataAPI, c.grant_id)
            .then(() => enqueueSnackbar(`Revoked ${c.client_name || c.client_id}.`, { variant: 'success' }))
            .catch((e) => {
                if (errorCode(e) === 'connection_not_found') {
                    enqueueSnackbar('That connection had already ended.', { variant: 'info' });
                } else {
                    enqueueSnackbar('Could not revoke the connection. Try again.', { variant: 'error' });
                }
            })
            .finally(() => {
                setRevoking(null);
                load();
            });
    };

    const code = errorCode(state.error);
    const status = state.error instanceof AIEditApiError ? state.error.status : null;

    return (
        <Box p={2}>
            <PageMeta title="MCP connections" />
            <Typography variant="h6">MCP connections</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                AI clients you have signed in to with this account, through MCP.
            </Typography>

            {state.loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {!state.loading && state.error && code === 'authserver_disabled' && (
                <Alert severity="info">MCP sign-in is not set up on this server, so there are no connections to show.</Alert>
            )}
            {!state.loading && state.error && code === 'tenant_required' && (
                <Alert severity="info">Curated Planet operations accounts manage their MCP connections in the Ops Console.</Alert>
            )}
            {!state.loading && state.error && code !== 'authserver_disabled' && code !== 'tenant_required' && (
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={load}>
                            Retry
                        </Button>
                    }
                >
                    {status === 502 ? 'MCP connections are unavailable right now.' : 'Could not load your MCP connections.'}
                </Alert>
            )}

            {!state.loading && !state.error && (
                <McpAddresses eligible={state.eligible} addresses={state.addresses} store={store} />
            )}

            {!state.loading && !state.error && state.connections.length === 0 && (
                <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="body2">No MCP clients are connected to your account.</Typography>
                    {state.eligible === true && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            A client (Claude, Cursor, …) appears here once you sign in to it with this account.
                        </Typography>
                    )}
                </Paper>
            )}

            {!state.loading && !state.error && state.connections.length > 0 && (
                <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small" aria-label="MCP connections">
                        <TableHead>
                            <TableRow>
                                <TableCell>Client</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell>MCP address</TableCell>
                                <TableCell>Access</TableCell>
                                <TableCell>Connected</TableCell>
                                <TableCell>Last used</TableCell>
                                <TableCell>Sign-in IP</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.connections.map((c) => (
                                <TableRow key={c.grant_id}>
                                    <TableCell sx={{ maxWidth: 360 }}>
                                        <ClientCell connection={c} />
                                    </TableCell>
                                    <TableCell>{grantCompany(c)}</TableCell>
                                    <TableCell sx={{ maxWidth: 320 }}>
                                        {c.resource ? <Mono>{c.resource}</Mono> : '—'}
                                        {c.stale && (
                                            <Stack spacing={0.25} sx={{ mt: 0.5, alignItems: 'flex-start' }}>
                                                <Chip size="small" variant="outlined" color="warning" label="Address no longer valid" />
                                                <Typography variant="caption" color="text.secondary">
                                                    Revoke it and connect again.
                                                </Typography>
                                            </Stack>
                                        )}
                                    </TableCell>
                                    <TableCell>{accessLabel(c.scopes)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(c.created_at)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {c.last_used_at ? (
                                            <Tooltip title={fmt(c.last_used_at)}>
                                                <span>{moment(c.last_used_at).fromNow()}</span>
                                            </Tooltip>
                                        ) : (
                                            'Not yet'
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{c.login_ip ?? '—'}</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            color="error"
                                            size="small"
                                            disabled={revoking === c.grant_id}
                                            onClick={() => setConfirm(c)}
                                            aria-label={`Revoke ${c.client_name || c.client_id}`}
                                        >
                                            Revoke
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
                <DialogTitle>{`Revoke ${confirm?.client_name || confirm?.client_id || 'this connection'}?`}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The client can no longer refresh its access. An access token it already holds stops working within 15
                        minutes. To use it again, connect it again from the client.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => {
                            const c = confirm;
                            setConfirm(null);
                            if (c) doRevoke(c);
                        }}
                    >
                        Revoke
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default McpConnectionsPage;
